/**
 * One rendering implementation for corporate-event risk, shared by every
 * surface that suggests a trade: the Roll tab, the Add-Spread tab, the symbol
 * page and the ideas page.
 *
 * Two treatments, and the split is deliberate:
 *
 *   - ASSIGNMENT RISK gets a row tint. It is genuinely per-row -- it depends on
 *     that candidate's own strike and mark against the dividend -- so it can be
 *     true of one row and false of its neighbour.
 *
 *   - PROXIMITY gets a boundary marker instead. Proximity is a SUFFIX: if a
 *     report falls between now and a 45-DTE expiry, it falls between now and
 *     every longer one too. A per-row tint therefore says exactly what a single
 *     line at row N says, at five rows of visual weight instead of one -- and
 *     with the next report 30 days out, five of six roll targets would tint.
 *
 * The sublabel renders on every affected row either way, so meaning never
 * depends on colour alone.
 */

export interface EventRisk {
  earningsInWindow: boolean;
  earningsDate: Date | null;
  earningsTiming: 'bmo' | 'amc' | null;
  exDivInWindow: boolean;
  exDivDate: Date | null;
  exDivAmount: number | null;
  exDivExceedsExtrinsic: boolean;
}

export interface CalendarCoverage {
  hasData: boolean;
  lastConfirmedAt: Date | null;
}

/**
 * Event dates are UTC-midnight instants. Local-time getters render the previous
 * day for every US timezone -- `new Date('2026-08-14T00:00:00Z').getDate()` is
 * 13 in America/New_York.
 */
function formatEventDate(date: Date): string {
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}

/** Only assignment risk earns a row tint. Proximity gets a boundary instead. */
export function eventRiskTint(risk: EventRisk | null | undefined): 'red' | null {
  return risk?.exDivExceedsExtrinsic ? 'red' : null;
}

/**
 * Index of the first row carrying each event kind. Callers compare a row's
 * index against these to decide whether it opens the exposure.
 *
 * Meaningful only when rows run in expiration order -- see
 * packages/api/src/routes/quotes.expiration-order.test.ts.
 */
export function firstEventRows(
  rows: Array<EventRisk | null | undefined>,
): { earnings: number | null; exDiv: number | null } {
  let earnings: number | null = null;
  let exDiv: number | null = null;
  rows.forEach((risk, i) => {
    if (!risk) return;
    if (earnings === null && risk.earningsInWindow) earnings = i;
    if (exDiv === null && risk.exDivInWindow) exDiv = i;
  });
  return { earnings, exDiv };
}

/**
 * Row classes for the event treatment alone. Callers compose this with their
 * own row classes and own any precedence against them.
 *
 * Emits at most one `border-t-*` colour: two in one class string is resolved by
 * CSS source order rather than string order, which is not something to leave to
 * chance.
 */
export function eventRowClasses(input: {
  risk: EventRisk | null | undefined;
  isEarningsBoundary: boolean;
  isExDivBoundary: boolean;
}): string {
  const classes: string[] = [];
  if (eventRiskTint(input.risk) === 'red') classes.push('bg-red-50/60');
  if (input.isEarningsBoundary || input.isExDivBoundary) {
    classes.push('border-t-2', 'border-t-amber-300');
  }
  return classes.join(' ');
}

/**
 * The sublabel, in two breakpoint variants. Chosen by breakpoint rather than a
 * toggle: no control to find, no preference to store, no state to get wrong.
 */
export function EventRiskLabel({
  risk,
  emphasis = false,
}: {
  risk: EventRisk | null | undefined;
  emphasis?: boolean;
}) {
  if (!risk) return null;
  const showEarnings = risk.earningsInWindow && risk.earningsDate != null;
  const showExDiv = risk.exDivInWindow && risk.exDivDate != null;
  if (!showEarnings && !showExDiv) return null;

  const long: string[] = [];
  const short: string[] = [];

  if (showEarnings) {
    const day = formatEventDate(risk.earningsDate!);
    long.push(`earnings ${day}${risk.earningsTiming ? ` ${risk.earningsTiming}` : ''}`);
    short.push(`ER ${day}`);
  }
  if (showExDiv) {
    const day = formatEventDate(risk.exDivDate!);
    long.push(`ex-div ${day}`);
    short.push(`div ${day}`);
  }

  // Ask eventRiskTint rather than re-testing the flag, so the label's colour and
  // its "assignment risk" suffix can never disagree with the row tint they are
  // there to explain. Widening what counts as tint-worthy is then one edit.
  const isAssignmentRisk = eventRiskTint(risk) === 'red';

  const tone = isAssignmentRisk
    ? 'text-red-600'
    : emphasis
      ? 'text-amber-700 font-medium'
      : 'text-gray-400';

  const longText = long.join(' · ') + (isAssignmentRisk ? ' — assignment risk' : '');
  const shortText = short.join(' · ');

  return (
    <div className={`text-[10px] ${tone}`}>
      <span className="hidden sm:inline">{longText}</span>
      <span className="sm:hidden">⚠ {shortText}</span>
    </div>
  );
}

/**
 * Shown when the nightly sweep has never recorded anything for this symbol.
 *
 * `coverage === undefined` renders nothing rather than assuming no data: an
 * older cached response simply lacks the field, and absent is not the same fact
 * as false.
 */
export function CalendarCoverageNotice({
  symbol,
  coverage,
}: {
  symbol: string;
  coverage: CalendarCoverage | undefined;
}) {
  if (!coverage || coverage.hasData) return null;
  return (
    <div className="text-xs text-gray-500 mb-2">
      No calendar data for {symbol} yet — earnings and dividend dates are collected nightly.
    </div>
  );
}
